import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useMotionTemplate } from 'motion/react';
import './TiltedCard.css';

const springValues = { damping: 30, stiffness: 100, mass: 2 };

export default function TiltedCard({
  imageSrc,
  altText           = 'Project image',
  captionText       = '',
  containerHeight   = '300px',
  containerWidth    = '100%',
  imageHeight       = '300px',
  imageWidth        = '100%',
  scaleOnHover      = 1.06,
  rotateAmplitude   = 14,
  showMobileWarning = false,
  showTooltip       = true,
  overlayContent    = null,
  displayOverlayContent = false,
  bgColor           = 'linear-gradient(135deg, #635bff 0%, #9a7fff 100%)',
}) {
  const ref = useRef(null);

  const x              = useMotionValue(0);
  const y              = useMotionValue(0);
  const shineX         = useMotionValue(50);
  const shineY         = useMotionValue(50);
  const rotateX        = useSpring(useMotionValue(0), springValues);
  const rotateY        = useSpring(useMotionValue(0), springValues);
  const scale          = useSpring(1, springValues);
  const opacity        = useSpring(0);
  const rotateFigcaption = useSpring(0, { stiffness: 350, damping: 30, mass: 1 });
  const [lastY, setLastY] = useState(0);

  const shineBg = useMotionTemplate`radial-gradient(circle at ${shineX}% ${shineY}%, rgba(255,255,255,0.22) 0%, transparent 65%)`;

  function handleMouse(e) {
    if (!ref.current) return;
    const rect    = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left  - rect.width  / 2;
    const offsetY = e.clientY - rect.top   - rect.height / 2;
    rotateX.set((offsetY / (rect.height / 2)) * -rotateAmplitude);
    rotateY.set((offsetX / (rect.width  / 2)) *  rotateAmplitude);
    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);
    shineX.set(((e.clientX - rect.left) / rect.width)  * 100);
    shineY.set(((e.clientY - rect.top)  / rect.height) * 100);
    rotateFigcaption.set(-(offsetY - lastY) * 0.6);
    setLastY(offsetY);
  }

  function handleMouseEnter() { scale.set(scaleOnHover); opacity.set(1); }

  function handleMouseLeave() {
    opacity.set(0); scale.set(1);
    rotateX.set(0); rotateY.set(0);
    rotateFigcaption.set(0);
  }

  return (
    <figure
      ref={ref}
      className="tilted-card-figure"
      style={{ height: containerHeight, width: containerWidth }}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="tilted-card-inner"
        style={{ width: imageWidth, height: imageHeight, rotateX, rotateY, scale }}
      >
        {imageSrc ? (
          <motion.img
            src={imageSrc}
            alt={altText}
            className="tilted-card-img"
            style={{ width: imageWidth, height: imageHeight }}
          />
        ) : (
          <motion.div
            className="tilted-card-img tilted-card-bg"
            style={{ width: imageWidth, height: imageHeight, background: bgColor }}
          />
        )}

        {/* Shine overlay */}
        <motion.div
          className="tilted-card-shine"
          style={{ background: shineBg, opacity }}
        />

        {displayOverlayContent && overlayContent && (
          <motion.div className="tilted-card-overlay">{overlayContent}</motion.div>
        )}
      </motion.div>

      {showTooltip && (
        <motion.figcaption
          className="tilted-card-caption"
          style={{ x, y, opacity, rotate: rotateFigcaption }}
        >
          {captionText}
        </motion.figcaption>
      )}
    </figure>
  );
}
